import { useEffect, useMemo, useRef, useState } from "react";

import { Check, ChevronsUpDown, X } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

/**
 * Searchable single-select combobox — the DS answer to "a Select, but with type-
 * ahead search and optional grouping" (e.g. picking a product from hundreds of
 * catalog items). Composed from the DS Popover + Input; emits the selected
 * option's `id` (or null when cleared).
 *
 * Each `ComboOption` carries a `label` (+ optional `subtitle`, group header,
 * right-aligned `badge`, and hidden `keywords` matched by search). Use plain
 * `<Select>` when the list is short and unsearchable; reach for this when it's long.
 *
 * Search is client-side over `options` by default. Pass `onSearchChange` when
 * the candidates come from a SERVER (e.g. the Cinch company picker, where the
 * book is far too large to preload): the callback fires as the user types, the
 * caller refetches, and the local filter still runs over whatever came back —
 * harmless when the server already matched, and it keeps typing responsive
 * while the request is in flight.
 */

export interface ComboOption {
  id: string;
  label: string;
  subtitle?: string;
  group?: string;
  badge?: string;
  /** Extra text matched by search (e.g. sku) but not shown. */
  keywords?: string;
}

export interface ComboboxProps {
  options: ComboOption[];
  value: string | null;
  onChange: (id: string | null) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyText?: string;
  clearable?: boolean;
  className?: string;
  disabled?: boolean;
  /** Fires as the user types. Provide for server-driven option lists. */
  onSearchChange?: (query: string) => void;
  /** Shown in place of `emptyText` while a server search is running. */
  loading?: boolean;
}

export function Combobox({
  options,
  value,
  onChange,
  placeholder = "Select…",
  searchPlaceholder = "Search…",
  emptyText = "No matches.",
  clearable = true,
  className,
  disabled,
  onSearchChange,
  loading = false,
}: ComboboxProps) {
  const [open, setOpen] = useState(false);
  const [query, setQueryState] = useState("");
  const setQuery = (next: string) => {
    setQueryState(next);
    onSearchChange?.(next);
  };
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Focus the search box when the popover opens (DS pattern: ref, not autoFocus).
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  // The popover content is portaled to <body>, so inside a Dialog it sits
  // OUTSIDE the modal's scroll-lock (react-remove-scroll) — which swallows wheel
  // + trackpad events over the list (dragging the scrollbar still works). Scroll
  // the list imperatively on wheel so it works regardless of the lock. deltaMode
  // 1 = lines, 2 = pages; 0 = pixels.
  useEffect(() => {
    const el = listRef.current;
    if (!open || !el) return;
    const onWheel = (e: WheelEvent) => {
      if (el.scrollHeight <= el.clientHeight) return; // nothing to scroll
      const factor = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? el.clientHeight : 1;
      el.scrollTop += e.deltaY * factor;
      e.preventDefault();
      e.stopPropagation();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [open]);

  const selected = options.find((o) => o.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) =>
      `${o.label} ${o.subtitle ?? ""} ${o.keywords ?? ""}`.toLowerCase().includes(q),
    );
  }, [options, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, ComboOption[]>();
    for (const o of filtered) {
      const g = o.group ?? "";
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(o);
    }
    return [...map.entries()];
  }, [filtered]);

  function pick(id: string) {
    onChange(id);
    setOpen(false);
    setQuery("");
  }

  return (
    <Popover
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn("w-full justify-between font-normal", !selected && "text-muted-foreground", className)}
        >
          <span className="truncate">{selected ? selected.label : placeholder}</span>
          <span className="ml-2 flex items-center gap-1">
            {clearable && selected && (
              <X
                className="h-3.5 w-3.5 opacity-50 hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
              />
            )}
            <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-64 p-0" align="start">
        <div className="border-b p-2">
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={searchPlaceholder}
            className="h-8"
          />
        </div>
        <div ref={listRef} className="max-h-72 overflow-y-auto p-1">
          {filtered.length === 0 ? (
            <p className="px-2 py-6 text-center text-sm text-muted-foreground">
              {loading ? "Searching…" : emptyText}
            </p>
          ) : (
            grouped.map(([group, opts]) => (
              <div key={group || "_"}>
                {group && (
                  <p className="px-2 pb-1 pt-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                    {group}
                  </p>
                )}
                {opts.map((o) => (
                  <button
                    key={o.id}
                    type="button"
                    onClick={() => pick(o.id)}
                    className={cn(
                      "flex w-full items-center justify-between gap-2 rounded-sm px-2 py-1.5 text-left text-sm hover:bg-accent",
                      o.id === value && "bg-accent",
                    )}
                  >
                    <span className="min-w-0">
                      <span className="flex items-center gap-1.5">
                        {o.id === value && <Check className="h-3.5 w-3.5 shrink-0" />}
                        <span className="truncate">{o.label}</span>
                      </span>
                      {o.subtitle && (
                        <span className="block truncate text-xs text-muted-foreground">{o.subtitle}</span>
                      )}
                    </span>
                    {o.badge && (
                      <Badge variant="outline" className="shrink-0">
                        {o.badge}
                      </Badge>
                    )}
                  </button>
                ))}
              </div>
            ))
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
